import { useCallback, useRef } from 'react';
import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { updateMarketingState, resetMarketingState } from "../../Store/marketingSlice"
import "./components/SharedSidebar";
import { Header } from "./components/Header"
import { ChatPanel } from "./components/ChatPanel"
import { InspirationModal } from "./components/InspirationModal"
import { CMOOverview } from "./screens/CMOOverview"
import { BrandAnalysis } from "./screens/BrandAnalysis"
import { BrandProfile } from "./screens/BrandProfile"
import { MarketingPlan } from "./screens/MarketingPlan"
import { AdConcepts } from "./screens/AdConcepts"
import { GenerateAds } from "./screens/GenerateAds"
import { Creatives } from "./screens/Creatives"
import { Performance } from "./screens/Performance"
import { SCREENS, productStageOptions, goalOptions } from "./constant";
import services from "../../API/index"
import { toastify } from "../Toast"
import ConfirmModal from "../Modals/ConfirmModal"
import useDarkSide from "../useDarkSide"
import ResponsivePanel from "../Layout/ResponsivePanel"

const wizardSteps = ["brand-profile", "marketing-plan", "ad-concepts", "generate-ads", "creatives", "performance"];

export function AIMarketingApp() {
    const [, setTheme] = useDarkSide()

    const location = useLocation()
    const marketingState = useSelector(state => state.marketing)
    const dispatch = useDispatch()

    const currentScreen = marketingState.currentScreen || location.state?.screen || SCREENS.CMO_OVERVIEW
    const setCurrentScreen = (val) => dispatch(updateMarketingState({ currentScreen: typeof val === 'function' ? val(marketingState.currentScreen) : val }))

    const [showInspirationModal, setShowInspirationModal] = useState(false)
    const [selectedInspirationCards, setSelectedInspirationCards] = useState([])

    const productStage = marketingState.productStage
    const setProductStage = (val) => dispatch(updateMarketingState({ productStage: typeof val === 'function' ? val(marketingState.productStage) : val }))

    const marketingGoal = marketingState.marketingGoal
    const setMarketingGoal = (val) => dispatch(updateMarketingState({ marketingGoal: typeof val === 'function' ? val(marketingState.marketingGoal) : val }))

    const adBudget = marketingState.adBudget
    const setAdBudget = (val) => dispatch(updateMarketingState({ adBudget: typeof val === 'function' ? val(marketingState.adBudget) : val }))

    const [showCreateForm, setShowCreateForm] = useState(false)

    const selectedPlanId = marketingState.selectedPlanId
    const setSelectedPlanId = (val) => dispatch(updateMarketingState({ selectedPlanId: typeof val === 'function' ? val(marketingState.selectedPlanId) : val }))

    const websiteUrl = marketingState.websiteUrl
    const setWebsiteUrl = (val) => dispatch(updateMarketingState({ websiteUrl: typeof val === 'function' ? val(marketingState.websiteUrl) : val }))

    const manualDescription = marketingState.manualDescription
    const setManualDescription = (val) => dispatch(updateMarketingState({ manualDescription: typeof val === 'function' ? val(marketingState.manualDescription) : val }))

    const [showManualInput, setShowManualInput] = useState(false)
    const [isAnalyzing, setIsAnalyzing] = useState(false)

    const aiCmoId = marketingState.aiCmoId
    const setAiCmoId = (val) => dispatch(updateMarketingState({ aiCmoId: typeof val === 'function' ? val(marketingState.aiCmoId) : val }))

    const marketingPlan = marketingState.marketingPlan
    const setMarketingPlan = (val) => dispatch(updateMarketingState({ marketingPlan: typeof val === 'function' ? val(marketingState.marketingPlan) : val }))

    const adConcepts = marketingState.adConcepts || []
    const setAdConcepts = (val) => dispatch(updateMarketingState({ adConcepts: typeof val === 'function' ? val(marketingState.adConcepts || []) : val }))

    const [isGeneratingConcepts, setIsGeneratingConcepts] = useState(false)
    const [regeneratingIds, setRegeneratingIds] = useState(new Set())
    const [generatingCampaignIds, setGeneratingCampaignIds] = useState(new Set())

    const generatedScript = marketingState.generatedScript
    const setGeneratedScript = (val) => dispatch(updateMarketingState({ generatedScript: typeof val === 'function' ? val(marketingState.generatedScript) : val }))

    const selectedConceptId = marketingState.selectedConceptId
    const setSelectedConceptId = (val) => dispatch(updateMarketingState({ selectedConceptId: typeof val === 'function' ? val(marketingState.selectedConceptId) : val }))

    const [generatingScriptId, setGeneratingScriptId] = useState(null)
    const [isImageFlow, setIsImageFlow] = useState(false)
    const [showPerformanceStep, setShowPerformanceStep] = useState(false)
    const [selectedAdId, setSelectedAdId] = useState(null)
    const [aiCmosData, setAiCmosData] = useState([])
    const [isLoadingCmoList, setIsLoadingCmoList] = useState(true)

    const cmoPlans = marketingState.cmoPlans || []
    const setCmoPlans = (val) => dispatch(updateMarketingState({ cmoPlans: typeof val === 'function' ? val(marketingState.cmoPlans || []) : val }))

    const [maxStepReached, setMaxStepReached] = useState(0)
    const [isEditingExisting, setIsEditingExisting] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [planIdToDelete, setPlanIdToDelete] = useState(null)
    const [isDeletingPlan, setIsDeletingPlan] = useState(false)



    useEffect(() => {
        const currentIndex = wizardSteps.indexOf(currentScreen);
        setMaxStepReached(current => Math.max(current, currentIndex));
    }, [currentScreen]);

    const fetchCmoList = useCallback(async () => {
        setIsLoadingCmoList(true)
        try {
            const response = await services.getCMOList()
            if (response?.data?.aiCmos) {
                setAiCmosData(response.data.aiCmos)
                const mappedPlans = response.data.aiCmos.map(cmo => ({
                    id: cmo._id,
                    name: cmo.brandId?.brandName || "New Brand",
                    description: cmo.brandId?.description || "No description",
                    website: cmo.brandId?.url || "",
                    logo: cmo.brandId?.logo?.src || null,
                    lastUpdated: new Date(cmo.updatedAt).toLocaleDateString(),
                    status: cmo.marketingPlan?.recommendedCampaigns?.length > 0 ? "active" : cmo.brandId ? "draft" : "new",
                    conceptsCount: cmo.adConcepts?.length || 0,
                    adsGenerated: 0,
                    productStage: cmo.productStage || "",
                    primaryMarketingGoal: cmo.primaryMarketingGoal || "",
                    monthlyBudget: cmo.monthlyBudget || 0,
                }))
                dispatch(updateMarketingState({ cmoPlans: mappedPlans }))

                if (mappedPlans.length === 0) {
                    setShowCreateForm(true)
                }
            } else {
                dispatch(updateMarketingState({ cmoPlans: [] }))
                setShowCreateForm(true)
            }
        } catch (error) {
            console.error("Failed to fetch CMO list:", error)
            dispatch(updateMarketingState({ cmoPlans: [] }))
        } finally {
            setIsLoadingCmoList(false)
        }
    }, [dispatch])

    useEffect(() => {
        if (currentScreen === SCREENS.CMO_OVERVIEW) {
            fetchCmoList()
        }
    }, [currentScreen, fetchCmoList])

    const brandProfile = marketingState.brandProfile || {
        name: "",
        industry: "",
        description: "",
        productGoal: "",
        help: "",
        differentiation: "",
        benefits: "",
        painPoints: "",
        features: "",
        audience: "",
        tone: "",
        colors: ["#F97316", "#000000", "#FFFFFF"],
        logo: null
    }
    const setBrandProfile = (val) => dispatch(updateMarketingState({ brandProfile: typeof val === 'function' ? val(marketingState.brandProfile) : val }))
    const pathname = location.pathname
    const resetParam = new URLSearchParams(location.search).get("reset")

    const handledResetKey = useRef(null);
    useEffect(() => {
        if (location.state?.screen && currentScreen !== location.state.screen && !location.state?.reset) {
            dispatch(updateMarketingState({ currentScreen: location.state.screen }))
        }

        if (location.state?.reset) {
            if (handledResetKey.current === location.key) return;
            handledResetKey.current = location.key;
            dispatch(resetMarketingState())
            dispatch(updateMarketingState({ currentScreen: SCREENS.CMO_OVERVIEW, selectedPlanId: null }))
            setShowCreateForm(false)
            fetchCmoList()
        } else if (pathname?.startsWith("/ai-marketing") || pathname?.startsWith("/app/marketing")) {
            // Initial load or transition from other path
            if (currentScreen === undefined) {
                dispatch(resetMarketingState())
                dispatch(updateMarketingState({ currentScreen: SCREENS.CMO_OVERVIEW, selectedPlanId: null }))
                setShowCreateForm(false)
            }
        }
    }, [pathname, resetParam, location.state?.screen, location.state?.reset, location.key, currentScreen, dispatch, fetchCmoList])

    const handleThemeToggle = () => {
        const currentTheme = localStorage.getItem("theme")
        const nextTheme = currentTheme === "dark" ? "light" : "dark"
        setTheme(nextTheme)
    }

    const showChatPanel = marketingState.showChatPanel
    const setShowChatPanel = (val) => dispatch(updateMarketingState({ showChatPanel: typeof val === 'function' ? val(marketingState.showChatPanel) : val }))

    useEffect(() => {
        dispatch(updateMarketingState({ showChatPanel: false }))
    }, [currentScreen, dispatch])

    const toggleInspirationCard = (cardId) => {
        setSelectedInspirationCards(prev => {
            if (prev.includes(cardId)) {
                return prev.filter(id => id !== cardId)
            }
            if (prev.length >= 5) return prev
            return [...prev, cardId]
        })
    }

    const toggleConceptSelection = (id) => {
        setAdConcepts((prev) =>
            prev.map((concept) =>
                concept.id === id
                    ? { ...concept, selected: !concept.selected }
                    : { ...concept, selected: false }
            )
        )
    }

    const handleAnalyzeBrand = async () => {
        if (!websiteUrl && !manualDescription) return
        setShowCreateForm(false)
        setIsAnalyzing(true)
        setCurrentScreen(SCREENS.BRAND_ANALYSIS)
    }

    const handleAnalysisComplete = (brandData) => {
        if (brandData) {
            const mappedProfile = {
                name: brandData.brandName || "New Brand",
                industry: brandData.industry || "",
                description: brandData.description || "",
                productGoal: brandData.mainJobOfProduct || "",
                help: brandData.howItHelps || "",
                differentiation: brandData.differentiation || "",
                benefits: Array.isArray(brandData.benefits) ? brandData.benefits.join(", ") : (brandData.benefits || ""),
                painPoints: Array.isArray(brandData.painPoints) ? brandData.painPoints.join(", ") : (brandData.painPoints || ""),
                features: Array.isArray(brandData.features) ? brandData.features.join(", ") : (brandData.features || ""),
                audience: brandData.targetMarket || "",
                tone: brandData.brandToneAndVoice || "",
                colors: brandData.brandColors || ["#F97316", "#000000", "#FFFFFF"],
                logo: brandData.logo || null
            }
            setBrandProfile(mappedProfile)

            const newPlan = {
                id: brandData._id || Date.now().toString(),
                name: mappedProfile.name,
                industry: mappedProfile.industry,
                website: websiteUrl || undefined,
                description: manualDescription || undefined,
                lastUpdated: "Just now",
                status: "draft",
                conceptsCount: 0,
                adsGenerated: 0,
            }
            setCmoPlans(prev => [newPlan, ...prev])
            setSelectedPlanId(newPlan.id)

            if (brandData._id) {
                localStorage.setItem("selectedBrandId", brandData._id);
            }
        }

        setIsAnalyzing(false)
        setCurrentScreen(SCREENS.BRAND_PROFILE)
    }

    const handleSelectPlan = (planId) => {
        const selectedCmo = aiCmosData.find(cmo => cmo._id === planId);

        if (selectedCmo) {
            setAiCmoId(selectedCmo._id)

            // Populate brandProfile
            if (selectedCmo.brandId) {
                setBrandProfile({
                    name: selectedCmo.brandId.brandName || "",
                    industry: selectedCmo.brandId.industry || "",
                    description: selectedCmo.brandId.description || "",
                    productGoal: selectedCmo.brandId.mainJobOfProduct || "",
                    help: selectedCmo.brandId.howItHelps || "",
                    differentiation: selectedCmo.brandId.differentiation || "",
                    benefits: Array.isArray(selectedCmo.brandId.benefits) ? selectedCmo.brandId.benefits.join(", ") : (selectedCmo.brandId.benefits || ""),
                    painPoints: Array.isArray(selectedCmo.brandId.painPoints) ? selectedCmo.brandId.painPoints.join(", ") : (selectedCmo.brandId.painPoints || ""),
                    features: Array.isArray(selectedCmo.brandId.features) ? selectedCmo.brandId.features.join(", ") : (selectedCmo.brandId.features || ""),
                    audience: selectedCmo.brandId.targetMarket || "",
                    tone: selectedCmo.brandId.brandToneAndVoice || "",
                    colors: selectedCmo.brandId.brandColors || ["#F97316", "#000000", "#FFFFFF"],
                    logo: selectedCmo.brandId.logo || null
                })
                setWebsiteUrl(selectedCmo.brandId.url || "")
                localStorage.setItem("selectedBrandId", selectedCmo.brandId._id);
            }

            // Populate inputs
            if (selectedCmo.productStage) {
                const stageStr = selectedCmo.productStage.toLowerCase();
                const foundStage = productStageOptions.find(o => o.label.toLowerCase() === stageStr);
                setProductStage(foundStage ? foundStage.id : selectedCmo.productStage);
            }
            if (selectedCmo.primaryMarketingGoal) {
                const goalStr = selectedCmo.primaryMarketingGoal.toLowerCase();
                const foundGoal = goalOptions.find(o => o.label.toLowerCase() === goalStr);
                setMarketingGoal(foundGoal ? foundGoal.id : selectedCmo.primaryMarketingGoal);
            }
            if (selectedCmo.monthlyBudget !== undefined) {
                if (selectedCmo.monthlyBudget === 500) setAdBudget("organic");
                else if (selectedCmo.monthlyBudget === 2500) setAdBudget("testing");
                else if (selectedCmo.monthlyBudget >= 7500) setAdBudget("scaling");
                else setAdBudget("testing");
            }

            // Populate marketing plan
            if (selectedCmo.marketingPlan && Object.keys(selectedCmo.marketingPlan).length > 0 && selectedCmo.marketingPlan.recommendedCampaigns?.length > 0) {
                setMarketingPlan(selectedCmo.marketingPlan);
            } else {
                setMarketingPlan(null);
            }

            // Populate ad concepts
            if (selectedCmo.adConcepts && selectedCmo.adConcepts.length > 0) {
                setAdConcepts(selectedCmo.adConcepts.map(c => ({
                    ...c,
                    id: c._id,
                    selected: c.isSelected || false
                })));
            } else {
                setAdConcepts([]);
            }
        }

        setSelectedPlanId(planId)
        setIsEditingExisting(true)
        setMaxStepReached(wizardSteps.length - 1)
        setCurrentScreen(SCREENS.BRAND_PROFILE)
    }

    const handleCreateNewPlan = () => {
        setShowCreateForm(true)
        setWebsiteUrl("")
        setManualDescription("")
        setShowManualInput(false)
        setProductStage("")
        setMarketingGoal("")
        setAdBudget(null)
        setAiCmoId(null)
        setIsEditingExisting(false)
        setMaxStepReached(0)
    }

    const handleUpdateBrandProfile = async () => {
        const brandId = localStorage.getItem("selectedBrandId")
        if (!brandId) {
            toastify("error", "No brand selected. Please analyze or select a brand first.")
            return
        }

        const payload = {
            brandName: brandProfile.name,
            industry: brandProfile.industry,
            description: brandProfile.description,
            mainJobOfProduct: brandProfile.productGoal,
            howItHelps: brandProfile.help,
            differentiation: brandProfile.differentiation,
            benefits: brandProfile.benefits ? brandProfile.benefits.split(",").map(b => b.trim()) : [],
            painPoints: brandProfile.painPoints ? brandProfile.painPoints.split(",").map(p => p.trim()) : [],
            features: brandProfile.features ? brandProfile.features.split(",").map(f => f.trim()) : [],
            targetMarket: brandProfile.audience,
            brandToneAndVoice: brandProfile.tone,
            brandColors: brandProfile.colors,
            url: websiteUrl || undefined,
            status: "confirmed",
            productStage: productStageOptions.find(o => o.id === productStage)?.label || productStage,
            primaryMarketingGoal: goalOptions.find(o => o.id === marketingGoal)?.label || marketingGoal,
            monthlyBudget: adBudget === "organic" ? 500 : adBudget === "testing" ? 2500 : adBudget === "scaling" ? 7500 : undefined,
        }

        if (brandProfile.logo) {
            payload.logo = brandProfile.logo
        }

        try {
            const updateResponse = await services.updateBrandProfile(brandId, payload)
            const newAiCmoId = updateResponse?.data?.aiCmoId
            if (newAiCmoId) {
                setAiCmoId(newAiCmoId)
            }

            // Immediately generate marketing plan
            const planResponse = await services.generateMarketingPlan({
                aiCmoId: newAiCmoId
            })

            if (planResponse?.data?.marketingPlan) {
                setMarketingPlan(planResponse.data.marketingPlan)
            }

            // Update local plan state
            setCmoPlans(prev => prev.map(p =>
                p.id === selectedPlanId ? { ...p, status: "active" } : p
            ))

            setCurrentScreen(SCREENS.MARKETING_PLAN)
        } catch (error) {
            console.error("Failed to update brand profile or generate plan:", error)
        }
    }

    const handleGenerateAdConcepts = async () => {
        if (!aiCmoId) return

        setIsGeneratingConcepts(true)
        try {
            const response = await services.generateAdConcepts({
                aiCmoId: aiCmoId
            })

            if (response?.data) {
                if (response.data.adConcepts) {
                    const concepts = response.data.adConcepts.map(c => ({
                        ...c,
                        id: c._id,
                        selected: false,
                    }))
                    setAdConcepts(concepts)
                }
                if (response.data.marketingPlan) {
                    setMarketingPlan(response.data.marketingPlan)
                }
            }

            setCurrentScreen(SCREENS.AD_CONCEPTS)
        } catch (error) {
            console.error("Failed to generate ad concepts:", error)
        } finally {
            setIsGeneratingConcepts(false)
        }
    }

    const handleGenerateCampaignConcepts = async (campaignId) => {
        if (!aiCmoId || !campaignId) return

        setGeneratingCampaignIds(prev => {
            const next = new Set(prev)
            next.add(campaignId)
            return next
        })

        try {
            const response = await services.generateCampaignConcepts({
                aiCmoId: aiCmoId,
                campaignId: campaignId
            })

            if (response?.data) {
                if (response.data.adConcepts) {
                    // We try to preserve the existing selection state since it's locally managed
                    const selectedIds = new Set(adConcepts.filter(c => c.selected).map(c => c.id || c._id))
                    const concepts = response.data.adConcepts.map(c => ({
                        ...c,
                        id: c._id,
                        selected: selectedIds.has(c._id),
                    }))
                    setAdConcepts(concepts)
                }
                if (response.data.marketingPlan) {
                    setMarketingPlan(response.data.marketingPlan)
                }
            }
        } catch (error) {
            console.error("Failed to generate campaign concepts:", error)
        } finally {
            setGeneratingCampaignIds(prev => {
                const next = new Set(prev)
                next.delete(campaignId)
                return next
            })
        }
    }

    const handleRegenerateConcept = async (conceptId) => {
        if (!aiCmoId || !conceptId) return

        setRegeneratingIds(prev => {
            const next = new Set(prev)
            next.add(conceptId)
            return next
        })

        try {
            const response = await services.regenerateConcept({
                aiCmoId: aiCmoId,
                conceptId: conceptId
            })

            if (response?.data) {
                const newConcept = response.data
                setAdConcepts(prev => prev.map(c =>
                    c.id === conceptId ? { ...newConcept, id: newConcept._id, selected: c.selected } : c
                ))
            }
        } catch (error) {
            console.error("Failed to regenerate concept:", error)
        } finally {
            setRegeneratingIds(prev => {
                const next = new Set(prev)
                next.delete(conceptId)
                return next
            })
        }
    }

    const handleEditConcept = async (conceptId, changes) => {
        if (!aiCmoId || !conceptId || !Object.keys(changes).length) return null

        const response = await services.editAdConcept({
            aiCmoId,
            conceptId,
            ...changes,
        })
        const updatedConcept = response?.data || response

        if (updatedConcept && typeof updatedConcept === "object") {
            setAdConcepts(prev => prev.map(concept => {
                const currentId = concept.id || concept._id
                if (currentId !== conceptId) return concept

                return {
                    ...updatedConcept,
                    id: updatedConcept._id || conceptId,
                    selected: concept.selected,
                }
            }))
        }

        return updatedConcept
    }

    const handleGenerateScript = async (conceptId) => {
        if (!aiCmoId || !conceptId) return;
        setIsImageFlow(false); // video concept path

        const concept = adConcepts.find(c => c.id === conceptId || c._id === conceptId);
        if (concept) {
            setGeneratedScript(concept.script || "");
            setSelectedConceptId(conceptId);
            setCurrentScreen(SCREENS.GENERATE_ADS);
        }
    }

    const showGeneratedCreatives = (filter) => {
        setIsImageFlow(filter === "image")
        localStorage.setItem("creativesActiveFilter", filter)
        setCurrentScreen(SCREENS.CREATIVES)
    }

    const handleGenerateImageAd = async (conceptId) => {
        if (!aiCmoId || !conceptId) return
        const brandId = localStorage.getItem("selectedBrandId")
        if (!brandId) {
            toastify("error", "No brand selected. Please complete the brand profile first.")
            return
        }

        setGeneratingScriptId(conceptId) // re-use loading state on button
        setIsImageFlow(true)  // mark as image ad flow
        try {
            // Find the concept to use its hook/concept text as the prompt
            const concept = adConcepts.find(c => c.id === conceptId || c._id === conceptId)
            const formData = new FormData()
            formData.append("type", "Ads")
            formData.append("userPrompt", concept?.hook || concept?.conceptDescription || concept?.concept || "")
            formData.append("numberOfAds", "1")
            formData.append("size", "1:1")
            formData.append("cmoId", aiCmoId)
            formData.append("conceptId", conceptId)

            await services.createImageAd(brandId, formData)
            setSelectedConceptId(conceptId)
            showGeneratedCreatives("image")
        } catch (error) {
            console.error("Failed to generate image ad:", error)
        } finally {
            setGeneratingScriptId(null)
        }
    }

    const handleDeletePlan = (planId) => {
        setPlanIdToDelete(planId)
        setShowDeleteConfirm(true)
    }

    const confirmDelete = async () => {
        if (!planIdToDelete) return
        setIsDeletingPlan(true)
        try {
            await services.deleteCMO(planIdToDelete)
            setCmoPlans(prev => prev.filter(p => p.id !== planIdToDelete))
            setShowDeleteConfirm(false)
            setPlanIdToDelete(null)
        } catch (error) {
            console.error("Failed to delete CMO project:", error)
        } finally {
            setIsDeletingPlan(false)
        }
    }

    const currentProjectName = selectedPlanId ? cmoPlans.find(p => p.id === selectedPlanId)?.name : null

    const renderScreen = () => {
        switch (currentScreen) {
            case SCREENS.CMO_OVERVIEW:
                return (
                    <CMOOverview
                        cmoPlans={cmoPlans}
                        isLoading={isLoadingCmoList}
                        onCreateNewPlan={handleCreateNewPlan}
                        onToggleChat={() => setShowChatPanel(prev => !prev)}
                        isChatOpen={showChatPanel}
                        onSelectPlan={handleSelectPlan}
                        showCreateForm={showCreateForm}
                        setShowCreateForm={setShowCreateForm}
                        websiteUrl={websiteUrl}
                        setWebsiteUrl={setWebsiteUrl}
                        manualDescription={manualDescription}
                        setManualDescription={setManualDescription}
                        showManualInput={showManualInput}
                        setShowManualInput={setShowManualInput}
                        onAnalyzeBrand={handleAnalyzeBrand}
                        onDeletePlan={handleDeletePlan}
                    />
                )

            case SCREENS.BRAND_ANALYSIS:
                return (
                    <BrandAnalysis
                        websiteUrl={websiteUrl}
                        manualDescription={manualDescription}
                        initialIsAnalyzing={isAnalyzing}
                        onAnalysisComplete={handleAnalysisComplete}
                    />
                )

            case SCREENS.BRAND_PROFILE:
                return (
                    <BrandProfile
                        brandProfile={brandProfile}
                        setBrandProfile={setBrandProfile}
                        productStage={productStage}
                        setProductStage={setProductStage}
                        marketingGoal={marketingGoal}
                        setMarketingGoal={setMarketingGoal}
                        adBudget={adBudget}
                        setAdBudget={setAdBudget}
                        currentProjectName={currentProjectName}
                        isEditing={!!aiCmoId}
                        onNext={handleUpdateBrandProfile}
                    />
                )

            case SCREENS.MARKETING_PLAN:
                return (
                    <MarketingPlan
                        productStage={productStage}
                        marketingPlan={marketingPlan}
                        onNext={handleGenerateAdConcepts}
                        isGenerating={isGeneratingConcepts}
                        onOpenInspiration={() => setShowInspirationModal(true)}
                        selectedInspirationCount={selectedInspirationCards.length}
                        currentProjectName={currentProjectName}
                    />
                )

            case SCREENS.AD_CONCEPTS:
                return (
                    <AdConcepts
                        concepts={adConcepts}
                        campaigns={marketingPlan?.recommendedCampaigns || []}
                        onGenerateCampaignConcepts={handleGenerateCampaignConcepts}
                        generatingCampaignIds={generatingCampaignIds}
                        onToggleConcept={toggleConceptSelection}
                        onSelect={handleGenerateScript}
                        onSelectImageConcept={handleGenerateImageAd}
                        onEdit={handleEditConcept}
                        onRegenerate={handleRegenerateConcept}
                        regeneratingIds={regeneratingIds}
                        generatingScriptId={generatingScriptId}
                        onBack={() => setCurrentScreen(SCREENS.MARKETING_PLAN)}
                        currentProjectName={currentProjectName}
                    />
                )

            case SCREENS.GENERATE_ADS:
                return (
                    <GenerateAds
                        projectName={currentProjectName}
                        initialScript={generatedScript}
                        aiCmoId={aiCmoId}
                        conceptId={selectedConceptId}
                        onBack={() => setCurrentScreen(SCREENS.AD_CONCEPTS)}
                        onGenerate={() => showGeneratedCreatives("video")}
                    />
                )

            case SCREENS.CREATIVES:
                return (
                    <Creatives
                        projectName={currentProjectName}
                        initialFilter={isImageFlow ? "image" : "video"}
                        onSelectVideo={() => setCurrentScreen(SCREENS.EDITOR)}
                        onBack={() => setCurrentScreen(SCREENS.AD_CONCEPTS)}
                        onShowPerformance={(adId) => {
                            setSelectedAdId(adId)
                            setShowPerformanceStep(true)
                            setCurrentScreen(SCREENS.PERFORMANCE)
                        }}
                    />
                )

            case SCREENS.PERFORMANCE:
                return (
                    <Performance
                        projectName={currentProjectName}
                        onBack={() => setCurrentScreen(SCREENS.CREATIVES)}
                        onUpdatePlan={(newConcept) => {
                            if (newConcept) {
                                // Add the new concept to the list and select it
                                setAdConcepts(prev => {
                                    const updated = prev.map(c => ({ ...c, selected: false }))
                                    return [{ ...newConcept, id: newConcept._id, selected: true }, ...updated]
                                })
                                setSelectedConceptId(newConcept._id)
                                setCurrentScreen(SCREENS.AD_CONCEPTS)
                            } else {
                                setCurrentScreen(SCREENS.MARKETING_PLAN)
                            }
                        }}
                        adId={selectedAdId}
                        aiCmoId={aiCmoId}
                    />
                )

            default:
                return (
                    <CMOOverview
                        cmoPlans={cmoPlans}
                        isLoading={isLoadingCmoList}
                        onCreateNewPlan={handleCreateNewPlan}
                        onToggleChat={() => setShowChatPanel(prev => !prev)}
                        isChatOpen={showChatPanel}
                        onSelectPlan={handleSelectPlan}
                        showCreateForm={showCreateForm}
                        setShowCreateForm={setShowCreateForm}
                        websiteUrl={websiteUrl}
                        setWebsiteUrl={setWebsiteUrl}
                        manualDescription={manualDescription}
                        setManualDescription={setManualDescription}
                        showManualInput={showManualInput}
                        setShowManualInput={setShowManualInput}
                        onAnalyzeBrand={handleAnalyzeBrand}
                        onDeletePlan={handleDeletePlan}
                    />
                )
        }
    }

    return (
        <div className="marketing-workbench flex h-full bg-gray-50 dark:bg-[#0a0a0a] overflow-hidden">
            <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
                <Header
                    currentScreen={currentScreen}
                    showChatPanel={showChatPanel}
                    onScreenChange={setCurrentScreen}
                    onThemeToggle={handleThemeToggle}
                    isImageFlow={isImageFlow}
                    showPerformanceStep={showPerformanceStep}
                    isEditing={isEditingExisting}
                    maxStepReached={maxStepReached}
                />

                <div className="marketing-workbench__content flex-1 flex overflow-hidden">
                    {renderScreen()}
                    {currentScreen !== SCREENS.GENERATE_ADS && currentScreen !== SCREENS.CREATIVES && (
                        <ResponsivePanel open={showChatPanel} onClose={() => setShowChatPanel(false)} side="right" label="AI CMO chat">
                            <ChatPanel onClose={() => setShowChatPanel(false)} currentScreen={currentScreen} isOpen={showChatPanel} />
                        </ResponsivePanel>
                    )}
                </div>
            </div>

            <InspirationModal
                open={showInspirationModal}
                onOpenChange={setShowInspirationModal}
                selectedCards={selectedInspirationCards}
                onToggleCard={toggleInspirationCard}
                onConfirm={() => setShowInspirationModal(false)}
            />
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Project"
                message="Are you sure you want to delete this AI CMO project? This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                isLoading={isDeletingPlan}
            />
        </div>
    )
}
